import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getProfileImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocationContext } from '../context/LocationContext';
import LocationSelect from './LocationSelect';
import { loadLoginPreferences } from '../services/loginPreferences';
import { isLocalDevHost } from '../utils/tenantHost';
import { usePermissions } from '../hooks/usePermissions';
import { NotificationProvider } from '../context/NotificationContext';
import NotificationPanel from './NotificationPanel';
import MessageInboxButton from './MessageInboxButton';
import UnreadCountBadge from './UnreadCountBadge';
import { useMessageUnread } from '../context/MessageUnreadContext';
import { canShowQaChecksNav } from '../qa/tenantAccess';
import {
  MY_RECORDS_PATHS,
  buildMainNavigation,
  buildMyRecordsItems,
  buildProfileMenuItems,
  myRecordsNavTrigger,
  reportsNavItem,
  signOutEntry,
} from './layout/navConfig';
import {
  NavTopLink,
  NavDropdownTrigger,
  NavDropdownLink,
  NavMobileLink,
  NavEmoji,
  MobileMenuToggle,
} from './layout/NavMenu';

export { useLocationContext } from '../context/LocationContext';

interface LayoutProps {
  children: React.ReactNode;
}

function isPathActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function isMessagesActive(pathname: string) {
  return pathname === '/messages' || pathname === '/my-messages';
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, logout } = useAuth();
  const {
    selectedLocationId,
    setSelectedLocationId,
    locations,
    locationsLoading,
    locationsRefreshing,
    isSelectedLocationInactive,
    employeeDetailsReportLocationLocked,
  } = useLocationContext();
  const {
    hasDashboard,
    hasTaskManagement,
    hasMessageManagement,
    hasHolidayManagement,
    hasIncidentManagement,
    hasEmployeeManagement,
    hasScheduling,
    hasAttendanceAdmin,
    hasTimecards,
    hasReports,
    hasSettings,
    hasJobMonitor,
    hasLeavePolicies,
    hasJobApplicantTracking,
    loading: permissionsLoading,
    hasPermission,
    securityCommandControlLogEnabled,
  } = usePermissions();
  const { unreadCount: messageUnreadCount } = useMessageUnread();
  const canShowMessages = hasPermission('my_messages') || hasMessageManagement;
  const showFeatureAccessLink = hasSettings;
  const showSecurityCommandControlLogLink =
    !permissionsLoading &&
    securityCommandControlLogEnabled &&
    hasPermission('security_command_control_log');

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMyRecordsOpen, setIsMyRecordsOpen] = useState(false);
  const myRecordsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (myRecordsRef.current && !myRecordsRef.current.contains(event.target as Node)) {
        setIsMyRecordsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isMyRecordsOpen || isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMyRecordsOpen, isProfileOpen]);

  const pathname = location.pathname;
  const isActive = (path: string) => isPathActive(pathname, path);
  const myRecordsActive = MY_RECORDS_PATHS.some((p) => isPathActive(pathname, p));

  const hostname = window.location.hostname;
  const loginPrefs = loadLoginPreferences();
  const tenantName = loginPrefs.lastCompanyName
    || loginPrefs.lastCompanyCode
    || (isLocalDevHost(hostname) ? 'Development' : '');

  const mainNavigation = useMemo(
    () => buildMainNavigation({
      hasDashboard,
      hasEmployeeManagement,
      hasScheduling,
      hasAttendanceAdmin,
      hasTimecards,
    }),
    [hasDashboard, hasEmployeeManagement, hasScheduling, hasAttendanceAdmin, hasTimecards],
  );

  const myRecordsItems = useMemo(
    () => buildMyRecordsItems({
      canShowMessages,
      hasMyLicenses: hasPermission('my_licenses'),
      hasMarketplaceProposals: hasPermission('marketplace_proposals'),
    }),
    [canShowMessages, hasPermission],
  );

  const profileMenuItems = useMemo(
    () => buildProfileMenuItems({
      hasSettings,
      hasLeavePolicies,
      hasHolidayManagement,
      hasIncidentManagement,
      showSecurityCommandControlLogLink,
      showFeatureAccessLink,
      hasTaskManagement,
      hasMessageManagement,
      hasJobMonitor,
      hasJobApplicantTracking,
      hasScheduling,
      hasQaChecks: canShowQaChecksNav(hasReports),
    }),
    [
      hasSettings,
      hasLeavePolicies,
      hasHolidayManagement,
      hasIncidentManagement,
      showSecurityCommandControlLogLink,
      showFeatureAccessLink,
      hasTaskManagement,
      hasMessageManagement,
      hasJobMonitor,
      hasJobApplicantTracking,
      hasScheduling,
      hasReports,
    ],
  );

  const closeMobile = () => setIsMobileMenuOpen(false);

  return (
    <NotificationProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-neutral-50">
        <nav className="relative z-50 flex-shrink-0 border-b border-neutral-200 bg-white shadow-md">
          <div className="mx-auto w-full max-w-[82%] px-4 sm:px-6">
            {/* Brand row */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Staffly" className="h-12 w-auto" />
                <span className="hidden rounded-full bg-blue-100 px-3 py-0.5 text-sm font-medium text-blue-800 sm:inline">
                  {tenantName || 'Staffly'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <MobileMenuToggle
                  open={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen((v) => !v)}
                />

                <div className="relative" ref={profileRef}>
                  <button
                    type="button"
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-neutral-50"
                  >
                    {employee?.profileImageUrl && getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ? (
                      <img
                        src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                        alt={`${employee.firstName} ${employee.lastName}`}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
                        {employee?.firstName?.[0]?.toUpperCase() || employee?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="hidden max-w-[140px] truncate text-sm font-medium text-neutral-700 lg:block">
                      {employee ? `${employee.firstName} ${employee.lastName}` : 'User'}
                    </span>
                    <ChevronDown className="hidden h-4 w-4 text-neutral-400 lg:block" strokeWidth={1.75} />
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 z-50 mt-1 w-72 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                      {profileMenuItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50"
                        >
                          <NavEmoji emoji={item.icon} />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      ))}
                      <hr className="my-1 border-neutral-100" />
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-700 transition-colors hover:bg-red-50"
                        onClick={async () => {
                          setIsProfileOpen(false);
                          try {
                            await logout();
                          } finally {
                            navigate('/login', { replace: true });
                          }
                        }}
                      >
                        <NavEmoji emoji={signOutEntry.icon} />
                        {signOutEntry.label}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main navigation row */}
            <div className="hidden items-end justify-between md:flex">
              <div className="flex flex-wrap items-end">
                {mainNavigation.map((item) => (
                  <NavTopLink key={item.href} item={item} active={isActive(item.href)} />
                ))}

                <div className="relative" ref={myRecordsRef}>
                  <NavDropdownTrigger
                    item={myRecordsNavTrigger}
                    active={myRecordsActive}
                    open={isMyRecordsOpen}
                    onClick={() => setIsMyRecordsOpen((v) => !v)}
                  />
                  {isMyRecordsOpen && (
                    <div className="absolute left-0 top-full z-50 mt-0 w-56 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                      {myRecordsItems.map((item) => (
                        <NavDropdownLink
                          key={item.href}
                          item={item}
                          active={
                            item.href === '/messages'
                              ? isMessagesActive(pathname)
                              : isActive(item.href)
                          }
                          onClick={() => setIsMyRecordsOpen(false)}
                          trailing={
                            item.href === '/messages' ? (
                              <UnreadCountBadge count={messageUnreadCount} />
                            ) : undefined
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>

                {hasReports && (
                  <NavTopLink
                    item={reportsNavItem}
                    active={pathname.startsWith('/reports')}
                  />
                )}
              </div>

              <div className="flex shrink-0 items-end gap-3 pb-1">
                {canShowMessages && <MessageInboxButton />}
                <NotificationPanel />
                <div className="flex items-center gap-1.5 pb-2">
                  <span className="text-base leading-none" aria-hidden>📍</span>
                  <LocationSelect
                    locations={locations}
                    value={employeeDetailsReportLocationLocked ? null : selectedLocationId}
                    onChange={setSelectedLocationId}
                    disabled={employeeDetailsReportLocationLocked}
                    loading={locationsLoading}
                    refreshing={locationsRefreshing}
                    title={
                      employeeDetailsReportLocationLocked
                        ? 'Location is fixed to All locations while Employee details report is open.'
                        : isSelectedLocationInactive
                          ? 'This location is inactive — view only.'
                          : undefined
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile navigation */}
          {isMobileMenuOpen && (
            <div className="absolute left-0 right-0 top-full z-50 border-t border-neutral-200 bg-white shadow-lg md:hidden">
              <div className="space-y-1 px-4 py-3">
                <div className="mb-2 flex items-center gap-3 border-b border-neutral-100 pb-3">
                  {canShowMessages && <MessageInboxButton />}
                  <NotificationPanel />
                </div>

                {mainNavigation.map((item) => (
                  <NavMobileLink
                    key={item.href}
                    item={item}
                    active={isActive(item.href)}
                    onClick={closeMobile}
                  />
                ))}

                <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                  My Records
                </div>
                {myRecordsItems.map((item) => (
                  <NavMobileLink
                    key={item.href}
                    item={item}
                    active={
                      item.href === '/messages'
                        ? isMessagesActive(pathname)
                        : isActive(item.href)
                    }
                    onClick={closeMobile}
                    indent
                    trailing={
                      item.href === '/messages' ? (
                        <UnreadCountBadge count={messageUnreadCount} />
                      ) : undefined
                    }
                  />
                ))}

                {hasReports && (
                  <NavMobileLink
                    item={reportsNavItem}
                    active={pathname.startsWith('/reports')}
                    onClick={closeMobile}
                  />
                )}

                <div className="border-t border-neutral-100 pt-3">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="text-base leading-none" aria-hidden>📍</span>
                    <div className="min-w-0 flex-1">
                      <LocationSelect
                        locations={locations}
                        value={employeeDetailsReportLocationLocked ? null : selectedLocationId}
                        onChange={setSelectedLocationId}
                        disabled={employeeDetailsReportLocationLocked}
                        loading={locationsLoading}
                        refreshing={locationsRefreshing}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>

        <main className={`min-h-0 flex-1 ${isMessagesActive(pathname) ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {children}
        </main>
      </div>
    </NotificationProvider>
  );
};

export default Layout;
